export interface GroupedError {
  message: string;
  count: number;
  timestamp: string;
  level: string;
  labels?: Record<string, string>;
  serviceName?: string;
}

interface LogRow {
  message?: string;
  level?: string;
  timestamp?: string;
  labels?: Record<string, string>;
}

const SIMPLIFIED_MESSAGE_MAX_LENGTH = 150;

export const getServiceName = (labels?: Record<string, string>): string => {
  if (!labels) return 'unknown';

  const { app, service_name, service, container, pod, namespace } = labels;

  if (app) return app;
  if (service_name || service) return (service_name || service);
  if (container) return container;
  if (pod) {
    const podParts = pod.split('-');
    return podParts.length > 1 ? podParts[0] : pod;
  }
  if (namespace) return `ns:${namespace}`;

  return 'unknown';
};

const simplifyMessage = (message: string): string => {
  return message
    .replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?/g, 'DATE_TIME')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, 'UUID')
    .replace(/\b\d+\.\d+\.\d+\.\d+\b/g, 'IP_ADDRESS')
    .replace(/\b\d+\b/g, 'NUMBER')
    .substring(0, SIMPLIFIED_MESSAGE_MAX_LENGTH);
};

export const groupErrorsByMessage = (
  rows: LogRow[],
  maxResults: number = 10,
  includeServiceName: boolean = false,
): GroupedError[] => {
  const errorLogs = rows.filter(row => {
    const level = String(row.level || '').toLowerCase();
    const message = String(row.message || '').toLowerCase();
    return level === 'error' || message.includes('error');
  });

  const errorGroups = new Map<string, GroupedError>();

  errorLogs.forEach(log => {
    const message = String(log.message || '');
    const simplifiedKey = simplifyMessage(message);

    if (!errorGroups.has(simplifiedKey)) {
      errorGroups.set(simplifiedKey, {
        message,
        count: 1,
        timestamp: log.timestamp || '',
        level: log.level || 'error',
        labels: log.labels,
        serviceName: includeServiceName ? getServiceName(log.labels) : undefined,
      });
    } else {
      const group = errorGroups.get(simplifiedKey)!;
      group.count++;

      const currentTs = new Date(group.timestamp).getTime();
      const newTs = new Date(log.timestamp || '').getTime();
      if (newTs > currentTs) {
        group.timestamp = log.timestamp || '';
      }
    }
  });

  return Array.from(errorGroups.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, maxResults);
};
